import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Hash, Megaphone, UserPlus, Users, UsersRound } from "lucide-react";
import { cn } from "@/lib/utils";

const MENU_WIDTH = 280;
const MENU_MAX_HEIGHT = 240;
const VIEWPORT_PAD = 8;
const CARET_GAP = 6;

/**
 * Floating autocomplete list for @user / @team / #channel suggestions.
 * Opens above the caret when there isn't enough room below (chat composers).
 */
export default function ReferenceSuggestionList({
  items = [],
  selectedIndex = 0,
  onSelect,
  onHover,
  clientRect,
  loading = false,
  emptyText = "No matches",
  kind = "mention",
}) {
  const listRef = useRef(null);
  const [coords, setCoords] = useState(null);

  useLayoutEffect(() => {
    if (!clientRect || typeof window === "undefined") {
      setCoords(null);
      return;
    }

    const measuredHeight = Math.min(
      MENU_MAX_HEIGHT,
      Math.max(listRef.current?.scrollHeight || 0, 120)
    );
    const spaceBelow = window.innerHeight - clientRect.bottom - VIEWPORT_PAD;
    const spaceAbove = clientRect.top - VIEWPORT_PAD;
    const openAbove =
      spaceBelow < Math.min(measuredHeight, MENU_MAX_HEIGHT) &&
      spaceAbove > spaceBelow;

    const maxHeight = Math.max(
      96,
      Math.min(
        MENU_MAX_HEIGHT,
        openAbove ? spaceAbove - CARET_GAP : spaceBelow - CARET_GAP
      )
    );

    const height = Math.min(
      maxHeight,
      listRef.current?.offsetHeight || measuredHeight
    );

    let left = clientRect.left;
    left = Math.min(left, window.innerWidth - MENU_WIDTH - VIEWPORT_PAD);
    left = Math.max(VIEWPORT_PAD, left);

    const top = openAbove
      ? Math.max(VIEWPORT_PAD, clientRect.top - CARET_GAP - height)
      : clientRect.bottom + CARET_GAP;

    setCoords({
      left,
      top,
      maxHeight,
      openAbove,
    });
  }, [clientRect, items, loading, emptyText]);

  useEffect(() => {
    const el = listRef.current?.querySelector('[data-selected="true"]');
    el?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex, coords]);

  if (!clientRect) return null;

  const style = {
    position: "fixed",
    left: coords?.left ?? Math.max(VIEWPORT_PAD, clientRect.left),
    top: coords?.top ?? clientRect.bottom + CARET_GAP,
    zIndex: 200,
    width: MENU_WIDTH,
    maxHeight: coords?.maxHeight ?? MENU_MAX_HEIGHT,
  };

  return createPortal(
    <div
      ref={listRef}
      role="listbox"
      className="overflow-y-auto rounded-md border border-border bg-popover text-popover-foreground shadow-lg"
      style={style}
    >
      {loading ? (
        <div className="px-3 py-2 text-xs text-muted-foreground">Searching…</div>
      ) : items.length === 0 ? (
        <div className="px-3 py-2 text-xs text-muted-foreground">{emptyText}</div>
      ) : (
        items.map((item, index) => {
          const selected = index === selectedIndex;
          const isInvite = item.kind === "invite";
          return (
            <button
              key={item.id}
              type="button"
              role="option"
              aria-selected={selected}
              data-selected={selected ? "true" : undefined}
              className={cn(
                "flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-accent",
                selected && "bg-accent"
              )}
              onMouseEnter={() => onHover?.(index)}
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect?.(item);
              }}
            >
              <span className="mt-0.5 shrink-0 text-muted-foreground">
                {kind === "channel" || item.kind === "channel" ? (
                  <Hash className="h-3.5 w-3.5" />
                ) : item.kind === "here" ? (
                  <Megaphone className="h-3.5 w-3.5" />
                ) : isInvite ? (
                  <UserPlus className="h-3.5 w-3.5" />
                ) : item.kind === "team" ? (
                  <UsersRound className="h-3.5 w-3.5" />
                ) : (
                  <Users className="h-3.5 w-3.5" />
                )}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{item.label}</span>
                {item.subtitle ? (
                  <span className="block truncate text-xs text-muted-foreground">
                    {item.subtitle}
                  </span>
                ) : null}
              </span>
            </button>
          );
        })
      )}
    </div>,
    document.body
  );
}
